import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface Employee {
  id: string
  employeeNumber: string
  firstName: string
  lastName: string
  email?: string
  phone?: string
  designation?: string
  department?: string
  employmentType?: string
  salary?: string
  salaryCurrency?: string
  isActive: boolean
  dateOfJoining?: string
  costCenter?: {
    id: string
    name: string
  }
}

interface PayrollEntry {
  id: string
  payrollNumber: string
  employee: Employee
  payrollPeriodStart: string
  payrollPeriodEnd: string
  paymentDate: string
  postingDate?: string
  baseSalary: string
  overtime: string
  bonuses: string
  allowances: string
  otherEarnings: string
  totalEarnings: string
  taxDeduction: string
  socialSecurity: string
  pension: string
  otherDeductions: string
  totalDeductions: string
  netPay: string
  currency: string
  status: 'DRAFT' | 'POSTED' | 'PAID' | 'CANCELLED'
  postedAt?: string
  paidAt?: string
  notes?: string
}

interface Account {
  id: string
  code: string
  name: string
}

interface CostCenter {
  id: string
  name: string
}

const Payroll: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [activeTab, setActiveTab] = useState<'employees' | 'payroll'>('employees')
  const [employees, setEmployees] = useState<Employee[]>([])
  const [payrollEntries, setPayrollEntries] = useState<PayrollEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showEmployeeModal, setShowEmployeeModal] = useState(false)
  const [showPayrollModal, setShowPayrollModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [selectedPayrollEntry, setSelectedPayrollEntry] = useState<PayrollEntry | null>(null)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [costCenters, setCostCenters] = useState<CostCenter[]>([])
  const [employeeFormData, setEmployeeFormData] = useState({
    employeeNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    designation: '',
    department: '',
    employmentType: '',
    salary: '',
    salaryCurrency: 'GBP',
    salaryAccountId: '',
    costCenterId: '',
    isActive: true,
  })
  const [payrollFormData, setPayrollFormData] = useState({
    employeeId: '',
    payrollPeriodStart: '',
    payrollPeriodEnd: '',
    paymentDate: '',
    baseSalary: '',
    overtime: '',
    bonuses: '',
    allowances: '',
    otherEarnings: '',
    taxDeduction: '',
    socialSecurity: '',
    pension: '',
    otherDeductions: '',
    salaryExpenseAccountId: '',
    taxPayableAccountId: '',
    socialSecurityAccountId: '',
    pensionAccountId: '',
    salaryPayableAccountId: '',
    paymentAccountId: '',
    costCenterId: '',
    projectId: '',
    notes: '',
  })

  useEffect(() => {
    if (activeTab === 'employees') {
      fetchEmployees()
    } else {
      fetchPayrollEntries()
    }
  }, [activeTab])

  useEffect(() => {
    fetchAccounts()
    fetchCostCenters()
  }, [])

  const fetchEmployees = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/employees')
      setEmployees(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching employees:', error)
      toast.error('Failed to load employees')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPayrollEntries = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/payroll-entries')
      setPayrollEntries(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching payroll entries:', error)
      toast.error('Failed to load payroll entries')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts')
      setAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const fetchCostCenters = async () => {
    try {
      const response = await axios.get('/api/erp/cost-centers')
      setCostCenters(response.data.data || [])
    } catch (error) {
      console.error('Error fetching cost centers:', error)
    }
  }

  const handleCreateEmployee = async () => {
    try {
      await axios.post('/api/erp/employees', employeeFormData)
      toast.success('Employee created successfully')
      setShowEmployeeModal(false)
      setEmployeeFormData({
        employeeNumber: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        designation: '',
        department: '',
        employmentType: '',
        salary: '',
        salaryCurrency: 'GBP',
        salaryAccountId: '',
        costCenterId: '',
        isActive: true,
      })
      fetchEmployees()
    } catch (error: any) {
      console.error('Error creating employee:', error)
      toast.error(error.response?.data?.error || 'Failed to create employee')
    }
  }

  const handleCreatePayrollEntry = async () => {
    try {
      const payload = {
        ...payrollFormData,
        baseSalary: parseFloat(payrollFormData.baseSalary) || 0,
        overtime: parseFloat(payrollFormData.overtime) || 0,
        bonuses: parseFloat(payrollFormData.bonuses) || 0,
        allowances: parseFloat(payrollFormData.allowances) || 0,
        otherEarnings: parseFloat(payrollFormData.otherEarnings) || 0,
        taxDeduction: parseFloat(payrollFormData.taxDeduction) || 0,
        socialSecurity: parseFloat(payrollFormData.socialSecurity) || 0,
        pension: parseFloat(payrollFormData.pension) || 0,
        otherDeductions: parseFloat(payrollFormData.otherDeductions) || 0,
      }
      await axios.post('/api/erp/payroll-entries', payload)
      toast.success('Payroll entry created successfully')
      setShowPayrollModal(false)
      resetPayrollForm()
      fetchPayrollEntries()
    } catch (error: any) {
      console.error('Error creating payroll entry:', error)
      toast.error(error.response?.data?.error || 'Failed to create payroll entry')
    }
  }

  const resetPayrollForm = () => {
    setPayrollFormData({
      employeeId: '',
      payrollPeriodStart: '',
      payrollPeriodEnd: '',
      paymentDate: '',
      baseSalary: '',
      overtime: '',
      bonuses: '',
      allowances: '',
      otherEarnings: '',
      taxDeduction: '',
      socialSecurity: '',
      pension: '',
      otherDeductions: '',
      salaryExpenseAccountId: '',
      taxPayableAccountId: '',
      socialSecurityAccountId: '',
      pensionAccountId: '',
      salaryPayableAccountId: '',
      paymentAccountId: '',
      costCenterId: '',
      projectId: '',
      notes: '',
    })
  }

  const handlePostPayroll = async (id: string) => {
    if (!confirm('Are you sure you want to post this payroll entry?')) return
    try {
      await axios.post(`/api/erp/payroll-entries/${id}/post`)
      toast.success('Payroll entry posted successfully')
      fetchPayrollEntries()
      if (selectedPayrollEntry?.id === id) {
        const response = await axios.get(`/api/erp/payroll-entries/${id}`)
        setSelectedPayrollEntry(response.data.data)
      }
    } catch (error: any) {
      console.error('Error posting payroll entry:', error)
      toast.error(error.response?.data?.error || 'Failed to post payroll entry')
    }
  }

  const handleMarkAsPaid = async (id: string) => {
    if (!confirm('Are you sure you want to mark this payroll entry as paid?')) return
    try {
      await axios.post(`/api/erp/payroll-entries/${id}/mark-paid`)
      toast.success('Payroll entry marked as paid')
      fetchPayrollEntries()
      if (selectedPayrollEntry?.id === id) {
        const response = await axios.get(`/api/erp/payroll-entries/${id}`)
        setSelectedPayrollEntry(response.data.data)
      }
    } catch (error: any) {
      console.error('Error marking payroll as paid:', error)
      toast.error(error.response?.data?.error || 'Failed to mark payroll as paid')
    }
  }

  const handleDeletePayroll = async (id: string) => {
    if (!confirm('Are you sure you want to delete this payroll entry?')) return
    try {
      await axios.delete(`/api/erp/payroll-entries/${id}`)
      toast.success('Payroll entry deleted successfully')
      fetchPayrollEntries()
      if (selectedPayrollEntry?.id === id) {
        setShowViewModal(false)
        setSelectedPayrollEntry(null)
      }
    } catch (error: any) {
      console.error('Error deleting payroll entry:', error)
      toast.error(error.response?.data?.error || 'Failed to delete payroll entry')
    }
  }

  const calculateTotals = () => {
    const baseSalary = parseFloat(payrollFormData.baseSalary) || 0
    const overtime = parseFloat(payrollFormData.overtime) || 0
    const bonuses = parseFloat(payrollFormData.bonuses) || 0
    const allowances = parseFloat(payrollFormData.allowances) || 0
    const otherEarnings = parseFloat(payrollFormData.otherEarnings) || 0
    const totalEarnings = baseSalary + overtime + bonuses + allowances + otherEarnings

    const taxDeduction = parseFloat(payrollFormData.taxDeduction) || 0
    const socialSecurity = parseFloat(payrollFormData.socialSecurity) || 0
    const pension = parseFloat(payrollFormData.pension) || 0
    const otherDeductions = parseFloat(payrollFormData.otherDeductions) || 0
    const totalDeductions = taxDeduction + socialSecurity + pension + otherDeductions

    const netPay = totalEarnings - totalDeductions

    return { totalEarnings, totalDeductions, netPay }
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_payroll', 'read')) {
    return <div className="p-6">You don't have permission to access this page.</div>
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Payroll Management</h1>
        <PageHelp articleKey="erp_payroll" />
      </div>

      {/* Tabs */}
      <div className="mb-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('employees')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'employees'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Employees
          </button>
          <button
            onClick={() => setActiveTab('payroll')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'payroll'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Payroll Entries
          </button>
        </nav>
      </div>

      {/* Employees Tab */}
      {activeTab === 'employees' && (
        <div>
          <div className="mb-4 flex justify-end">
            {hasPermission('erp_payroll', 'write') && (
              <button
                onClick={() => {
                  setSelectedEmployee(null)
                  setEmployeeFormData({
                    employeeNumber: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    designation: '',
                    department: '',
                    employmentType: '',
                    salary: '',
                    salaryCurrency: 'GBP',
                    salaryAccountId: '',
                    costCenterId: '',
                    isActive: true,
                  })
                  setShowEmployeeModal(true)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Employee
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-8">Loading employees...</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Designation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Salary
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employees.map((employee) => (
                    <tr key={employee.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {employee.employeeNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {employee.firstName} {employee.lastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee.department || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee.designation || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee.salary
                          ? `${employee.salaryCurrency || 'GBP'} ${parseFloat(employee.salary).toFixed(2)}`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            employee.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {employee.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedEmployee(employee)
                            setShowViewModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {employees.length === 0 && (
                <div className="text-center py-8 text-gray-500">No employees found</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Payroll Entries Tab */}
      {activeTab === 'payroll' && (
        <div>
          <div className="mb-4 flex justify-end">
            {hasPermission('erp_payroll', 'write') && (
              <button
                onClick={() => {
                  setSelectedPayrollEntry(null)
                  resetPayrollForm()
                  setShowPayrollModal(true)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Create Payroll Entry
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-8">Loading payroll entries...</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payroll Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Period
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Earnings
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Deductions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Net Pay
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payrollEntries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {entry.payrollNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.employee.firstName} {entry.employee.lastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(entry.payrollPeriodStart).toLocaleDateString()} -{' '}
                        {new Date(entry.payrollPeriodEnd).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.currency} {parseFloat(entry.totalEarnings).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.currency} {parseFloat(entry.totalDeductions).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {entry.currency} {parseFloat(entry.netPay).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            entry.status === 'PAID'
                              ? 'bg-green-100 text-green-800'
                              : entry.status === 'POSTED'
                              ? 'bg-blue-100 text-blue-800'
                              : entry.status === 'DRAFT'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedPayrollEntry(entry)
                            setShowViewModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </button>
                        {entry.status === 'DRAFT' && hasPermission('erp_payroll', 'post') && (
                          <button
                            onClick={() => handlePostPayroll(entry.id)}
                            className="text-green-600 hover:text-green-900 mr-3"
                          >
                            Post
                          </button>
                        )}
                        {entry.status === 'POSTED' && hasPermission('erp_payroll', 'write') && (
                          <button
                            onClick={() => handleMarkAsPaid(entry.id)}
                            className="text-purple-600 hover:text-purple-900 mr-3"
                          >
                            Mark Paid
                          </button>
                        )}
                        {entry.status === 'DRAFT' && hasPermission('erp_payroll', 'delete') && (
                          <button
                            onClick={() => handleDeletePayroll(entry.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {payrollEntries.length === 0 && (
                <div className="text-center py-8 text-gray-500">No payroll entries found</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create Employee Modal */}
      {showEmployeeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Employee</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee Number *
                  </label>
                  <input
                    type="text"
                    value={employeeFormData.employeeNumber}
                    onChange={(e) =>
                      setEmployeeFormData({ ...employeeFormData, employeeNumber: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={employeeFormData.firstName}
                    onChange={(e) =>
                      setEmployeeFormData({ ...employeeFormData, firstName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={employeeFormData.lastName}
                    onChange={(e) =>
                      setEmployeeFormData({ ...employeeFormData, lastName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={employeeFormData.email}
                    onChange={(e) =>
                      setEmployeeFormData({ ...employeeFormData, email: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={employeeFormData.phone}
                    onChange={(e) =>
                      setEmployeeFormData({ ...employeeFormData, phone: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                  <input
                    type="text"
                    value={employeeFormData.designation}
                    onChange={(e) =>
                      setEmployeeFormData({ ...employeeFormData, designation: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <input
                    type="text"
                    value={employeeFormData.department}
                    onChange={(e) =>
                      setEmployeeFormData({ ...employeeFormData, department: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salary</label>
                  <input
                    type="number"
                    step="0.01"
                    value={employeeFormData.salary}
                    onChange={(e) =>
                      setEmployeeFormData({ ...employeeFormData, salary: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost Center</label>
                  <select
                    value={employeeFormData.costCenterId}
                    onChange={(e) =>
                      setEmployeeFormData({ ...employeeFormData, costCenterId: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select Cost Center</option>
                    {costCenters.map((cc) => (
                      <option key={cc.id} value={cc.id}>
                        {cc.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowEmployeeModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateEmployee}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Payroll Entry Modal */}
      {showPayrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Payroll Entry</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                  <select
                    value={payrollFormData.employeeId}
                    onChange={(e) =>
                      setPayrollFormData({ ...payrollFormData, employeeId: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees
                      .filter((e) => e.isActive)
                      .map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.employeeNumber} - {emp.firstName} {emp.lastName}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payroll Period Start *
                  </label>
                  <input
                    type="date"
                    value={payrollFormData.payrollPeriodStart}
                    onChange={(e) =>
                      setPayrollFormData({ ...payrollFormData, payrollPeriodStart: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payroll Period End *
                  </label>
                  <input
                    type="date"
                    value={payrollFormData.payrollPeriodEnd}
                    onChange={(e) =>
                      setPayrollFormData({ ...payrollFormData, payrollPeriodEnd: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                  <input
                    type="date"
                    value={payrollFormData.paymentDate}
                    onChange={(e) =>
                      setPayrollFormData({ ...payrollFormData, paymentDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold mb-3">Earnings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={payrollFormData.baseSalary}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, baseSalary: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Overtime</label>
                    <input
                      type="number"
                      step="0.01"
                      value={payrollFormData.overtime}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, overtime: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bonuses</label>
                    <input
                      type="number"
                      step="0.01"
                      value={payrollFormData.bonuses}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, bonuses: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowances</label>
                    <input
                      type="number"
                      step="0.01"
                      value={payrollFormData.allowances}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, allowances: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Other Earnings</label>
                    <input
                      type="number"
                      step="0.01"
                      value={payrollFormData.otherEarnings}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, otherEarnings: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold mb-3">Deductions</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax Deduction</label>
                    <input
                      type="number"
                      step="0.01"
                      value={payrollFormData.taxDeduction}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, taxDeduction: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Social Security</label>
                    <input
                      type="number"
                      step="0.01"
                      value={payrollFormData.socialSecurity}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, socialSecurity: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pension</label>
                    <input
                      type="number"
                      step="0.01"
                      value={payrollFormData.pension}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, pension: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Other Deductions</label>
                    <input
                      type="number"
                      step="0.01"
                      value={payrollFormData.otherDeductions}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, otherDeductions: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold mb-3">Totals</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Earnings</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {calculateTotals().totalEarnings.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Deductions</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {calculateTotals().totalDeductions.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Net Pay</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md font-semibold">
                      {calculateTotals().netPay.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold mb-3">Accounts</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Salary Expense Account *
                    </label>
                    <select
                      value={payrollFormData.salaryExpenseAccountId}
                      onChange={(e) =>
                        setPayrollFormData({
                          ...payrollFormData,
                          salaryExpenseAccountId: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select Account</option>
                      {accounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.code} - {acc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Salary Payable Account *
                    </label>
                    <select
                      value={payrollFormData.salaryPayableAccountId}
                      onChange={(e) =>
                        setPayrollFormData({
                          ...payrollFormData,
                          salaryPayableAccountId: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select Account</option>
                      {accounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.code} - {acc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax Payable Account</label>
                    <select
                      value={payrollFormData.taxPayableAccountId}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, taxPayableAccountId: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select Account</option>
                      {accounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.code} - {acc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Social Security Account
                    </label>
                    <select
                      value={payrollFormData.socialSecurityAccountId}
                      onChange={(e) =>
                        setPayrollFormData({
                          ...payrollFormData,
                          socialSecurityAccountId: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select Account</option>
                      {accounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.code} - {acc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pension Account</label>
                    <select
                      value={payrollFormData.pensionAccountId}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, pensionAccountId: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select Account</option>
                      {accounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.code} - {acc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Account</label>
                    <select
                      value={payrollFormData.paymentAccountId}
                      onChange={(e) =>
                        setPayrollFormData({ ...payrollFormData, paymentAccountId: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select Account</option>
                      {accounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.code} - {acc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowPayrollModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePayrollEntry}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Payroll Entry Modal */}
      {showViewModal && selectedPayrollEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Payroll Entry Details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payroll Number</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedPayrollEntry.payrollNumber}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedPayrollEntry.employee.firstName} {selectedPayrollEntry.employee.lastName} (
                    {selectedPayrollEntry.employee.employeeNumber})
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {new Date(selectedPayrollEntry.payrollPeriodStart).toLocaleDateString()} -{' '}
                    {new Date(selectedPayrollEntry.payrollPeriodEnd).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {new Date(selectedPayrollEntry.paymentDate).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        selectedPayrollEntry.status === 'PAID'
                          ? 'bg-green-100 text-green-800'
                          : selectedPayrollEntry.status === 'POSTED'
                          ? 'bg-blue-100 text-blue-800'
                          : selectedPayrollEntry.status === 'DRAFT'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {selectedPayrollEntry.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold mb-3">Earnings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {selectedPayrollEntry.currency} {parseFloat(selectedPayrollEntry.baseSalary).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Overtime</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {selectedPayrollEntry.currency} {parseFloat(selectedPayrollEntry.overtime).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bonuses</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {selectedPayrollEntry.currency} {parseFloat(selectedPayrollEntry.bonuses).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowances</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {selectedPayrollEntry.currency} {parseFloat(selectedPayrollEntry.allowances).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Other Earnings</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {selectedPayrollEntry.currency}{' '}
                      {parseFloat(selectedPayrollEntry.otherEarnings).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Earnings</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md font-semibold">
                      {selectedPayrollEntry.currency} {parseFloat(selectedPayrollEntry.totalEarnings).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold mb-3">Deductions</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax Deduction</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {selectedPayrollEntry.currency} {parseFloat(selectedPayrollEntry.taxDeduction).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Social Security</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {selectedPayrollEntry.currency}{' '}
                      {parseFloat(selectedPayrollEntry.socialSecurity).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pension</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {selectedPayrollEntry.currency} {parseFloat(selectedPayrollEntry.pension).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Other Deductions</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                      {selectedPayrollEntry.currency}{' '}
                      {parseFloat(selectedPayrollEntry.otherDeductions).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Deductions</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md font-semibold">
                      {selectedPayrollEntry.currency}{' '}
                      {parseFloat(selectedPayrollEntry.totalDeductions).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Net Pay</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md font-bold text-lg">
                      {selectedPayrollEntry.currency} {parseFloat(selectedPayrollEntry.netPay).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {selectedPayrollEntry.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {selectedPayrollEntry.notes}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 mt-6">
                {selectedPayrollEntry.status === 'DRAFT' && hasPermission('erp_payroll', 'post') && (
                  <button
                    onClick={() => handlePostPayroll(selectedPayrollEntry.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Post
                  </button>
                )}
                {selectedPayrollEntry.status === 'POSTED' && hasPermission('erp_payroll', 'write') && (
                  <button
                    onClick={() => handleMarkAsPaid(selectedPayrollEntry.id)}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    Mark Paid
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setSelectedPayrollEntry(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Payroll
